import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getData, updateData, getcountries } from '../../../services/admin/abstractReceipt/abReviewersServices';
import { Modal, Button, Table, Form } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import Header from '../../../components/Layout/Header';
import Footer from '../../../components/Layout/Footer';
import Sidebar from '../../../components/Layout/Sidebar';
import Swal from 'sweetalert2';
import Spinner from '../../../components/Spinner';

// ── Identify the current management user ─────────────────────────────────────
const currentUserName = `admin`;

// ── Parse remarks safely ─────────────────────────────────────────────────────
const parseRemarks = (raw) => {
  if (!raw) return [];
  try {
    if (Array.isArray(raw)) return raw;
    let parsed = JSON.parse(raw);
    if (typeof parsed === 'string') parsed = JSON.parse(parsed);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// ── Format ISO → "22 Jun 2026, 10:30 AM" ────────────────────────────────────
const formatTimestamp = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const PendingReviewers = () => {
  const [dataList, setDataList] = useState([]);
  const [form, setForm] = useState({
    id: '', reviewer_name: '', reviewer_affiliation: '',
    reviewer_country: '', sub_domain: '', series: '',
    reviewer_signature: '', status: 0, approval: 0, remarks: null,
  });
  const [modalType, setModalType]   = useState('');
  const [showModal, setShowModal]   = useState(false);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading]       = useState(true);
  const [dropdownData, setDropdownData] = useState({ countries: [] });

  // ── Remarks state ─────────────────────────────────────────────────────────
  const [remarks,        setRemarks]        = useState('');
  const [remarksHistory, setRemarksHistory] = useState([]);
  const [remarksError,   setRemarksError]   = useState('');

  const fetchData = useCallback(async () => {
    try {
      const data = await getData({ approval: 0 });
      setDataList(data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, []);

  const fetchDropdownData = useCallback(async () => {
    try {
      const [country] = await Promise.all([getcountries()]);
      setDropdownData({ countries: country });
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  }, []);

  useEffect(() => {
    const fetchDataAsync = async () => {
      setLoading(true);
      await fetchData();
      await fetchDropdownData();
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setLoading(false);
    };
    fetchDataAsync();
  }, [fetchData, fetchDropdownData]);

  const handleSubmit = async () => {
    // Remarks mandatory only when history exists
    if (remarksHistory.length > 0 && !remarks.trim()) {
      setRemarksError('Remarks is required');
      return;
    }

    try {
      if (modalType === 'edit') {
        let updatedRemarks = [...remarksHistory];

        if (remarks.trim()) {
          updatedRemarks = [
            ...remarksHistory,
            {
              remark:    remarks.trim(),
              timestamp: new Date().toISOString(),
              user:      currentUserName,
            },
          ];
        }

        const updatedForm = {
          ...form,
          status: 1,
          approval: 1,
          remarks: JSON.stringify(updatedRemarks),
        };
        await updateData(updatedForm);
      }
      fetchData();
      resetModal();
      Swal.fire({
        icon: 'success', title: 'Success',
        text: `Reviewer ${modalType === 'add' ? 'added' : 'Approved'} successfully!`,
        timer: 2000, showConfirmButton: false,
      });
    } catch (error) {
      console.error('Error during form submission:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Something went wrong while submitting the form!' });
    }
  };

  const openModal = (type, data = { id: '' }) => {
    setModalType(type);
    setForm(data);

    // ── Parse and reset remarks ───────────────────────────────────────────
    setRemarksHistory(parseRemarks(data.remarks));
    setRemarks('');
    setRemarksError('');

    setShowModal(true);
  };

  const resetModal = () => {
    setShowModal(false);
    setRemarks('');
    setRemarksHistory([]);
    setRemarksError('');
  };

  const handleSearchChange = (e) => setSearchText(e.target.value);
  const handlePageChange   = (page) => setCurrentPage(page);

  const getCountryName = (code) => {
    const country = dropdownData.countries.find(c => c.code === code);
    return country ? country.name : code;
  };

  const filteredData = useMemo(() => {
    return dataList
      .filter((data) =>
        data.reviewer_name.toLowerCase().includes(searchText.toLowerCase()) ||
        data.sub_domain.toLowerCase().includes(searchText.toLowerCase())
      )
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  }, [dataList, searchText]);

  const formatSeries = (series) => {
    const date = new Date(series);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const columns = [
    { name: '#',         selector: (row, i) => (currentPage - 1) * 10 + (i + 1), sortable: false, width: '10%' },
    { name: 'SubDomain', selector: (row) => row.sub_domain,                       sortable: true,  width: '10%', wrap: true },
    { name: 'Series',    selector: (row) => formatSeries(row.series),             sortable: true,  width: '10%', wrap: true },
    { name: 'Name',      selector: (row) => row.reviewer_name,                    sortable: true,  width: '15%', wrap: true },
    { name: 'Country',   selector: (row) => getCountryName(row.reviewer_country || 'N/A'), sortable: true, width: '10%', wrap: true },
    {
      name: 'Signature',
      cell: (row) => (
        <a href={row.reviewer_signature} target="_blank" rel="noopener noreferrer">
          <img src={row.reviewer_signature} width="100" alt={row.reviewer_affiliation} style={{ borderRadius: '5px', cursor: 'pointer' }} />
        </a>
      ),
      sortable: false, width: '15%', wrap: true,
    },
    {
      name: 'Status',
      cell: (row) => (
        <span className={`badge ${Number(row.status) === 1 ? 'bg-success' : 'bg-danger'}`}>
          {Number(row.status) === 1 ? 'Active' : 'Inactive'}
        </span>
      ),
      sortable: true, width: '10%', wrap: true,
    },
    {
      name: 'Actions',
      cell: (row) => (
        <Button className="me-2" variant="warning" size="sm" onClick={() => openModal('edit', row)}>Edit</Button>
      ),
      width: '15%', wrap: true,
    },
  ];

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />
        {loading ? (
          <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '300px' }}>
            <Spinner />
          </div>
        ) : (
          <div>
            <div className="container-fluid p-4">
              <div className="row">
                <div className="col-8">
                  <p className="fs-4 text-dark">PDF Generation / Abstract Receipt / Reviewer / Pending Reviewers</p>
                </div>
              </div>

              <DataTable
                columns={columns}
                data={filteredData}
                pagination
                highlightOnHover
                responsive
                striped
                paginationPerPage={10}
                paginationComponentOptions={{ noRowsPerPage: true }}
                onChangePage={handlePageChange}
                subHeader
                subHeaderComponent={
                  <input className="form-control" type="text" placeholder="Search"
                    value={searchText} onChange={handleSearchChange} />
                }
              />

              {/* ── Modal ── */}
              <Modal className="modal-lg" backdrop="static" show={showModal} onHide={resetModal}>
                <Modal.Header closeButton>
                  <Modal.Title>{modalType === 'add' ? 'Add' : 'Edit'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>

                  {/* Reviewer Details Table */}
                  <Table  bordered hover responsive className="table-sm">
                    <thead>
                      <tr>
                        <th className="bg-secondary text-white" colSpan={2}>Reviewer Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>Reviewer Name</td><td>{form.reviewer_name}</td></tr>
                      <tr><td>Reviewer Affiliation</td><td>{form.reviewer_affiliation}</td></tr>
                      <tr><td>Reviewer Country</td><td>{getCountryName(form.reviewer_country)}</td></tr>
                      <tr>
                      <td style={{ backgroundColor: '#e7e8eb' }}>Signature</td>
                      <td style={{ backgroundColor: '#e7e8eb' }}>
                        <img
                          src={form.reviewer_signature}
                          width="300"
                          alt="Reviewer Signature"
                        />
                      </td>
                    </tr>
                    </tbody>
                  </Table>

                  {/* ── Remarks History ── */}
                  {remarksHistory.length > 0 && (
                    <div className="mt-3">
                      <p className="fs-5 text-dark fw-semibold mb-2">
                        Remarks History
                        <span className="badge bg-secondary ms-2" style={{ fontSize: '0.72rem' }}>
                          {remarksHistory.length}
                        </span>
                      </p>
                      <div
                        className="border rounded"
                        style={{ maxHeight: '200px', overflowY: 'auto', backgroundColor: '#f8f9fa' }}
                      >
                        {[...remarksHistory].reverse().map((entry, idx) => (
                          <div
                            key={idx}
                            className="px-3 py-2 border-bottom"
                            style={{ borderLeft: '3px solid #0d6efd' }}
                          >
                            <div className="d-flex justify-content-between align-items-baseline">
                              <small className="text-muted d-block mb-1">
                                🕒 {formatTimestamp(entry.timestamp)}
                              </small>
                              {entry.user && (
                                <span className="badge bg-info text-dark" style={{ fontSize: '0.72rem' }}>
                                  {entry.user}
                                </span>
                              )}
                            </div>
                            <p className="mb-0 text-dark" style={{ fontSize: '0.9rem' }}>
                              {entry.remark}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── Remarks input ── */}
                  <Form.Group className="mt-3">
                    <Form.Label className="fs-5 text-dark">
                      Remarks{remarksHistory.length > 0 && <span className="text-danger fs-4"> * </span>}
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={remarks}
                      placeholder="Enter remarks…"
                      onChange={(e) => {
                        setRemarks(e.target.value);
                        setRemarksError('');
                      }}
                      isInvalid={!!remarksError}
                    />
                    <Form.Control.Feedback type="invalid">
                      {remarksError}
                    </Form.Control.Feedback>
                  </Form.Group>

                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={resetModal}>Cancel</Button>
                  <Button variant="primary" onClick={handleSubmit}>
                    {modalType === 'add' ? 'Add' : 'Approved'}
                  </Button>
                </Modal.Footer>
              </Modal>
            </div>
            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingReviewers;
